import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Armchair } from "lucide-react";
import { BoardPreview } from "../components/BoardPreview";
import { Ticker } from "../components/Ticker";
import { Reveal } from "../components/Reveal";
import { ScoreBar } from "../components/ScoreBar";
import { SampleTag } from "../components/SampleTag";
import { BOARD_TABS, HOW_IT_WORKS, REPORT } from "../data/sampleData";

const EASE = [0.22, 1, 0.36, 1];

const heroLine = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.2, delay: 0.15 + i * 0.18, ease: EASE },
    }),
};

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1, delay, ease: EASE },
});

const Home = () => {
    const heroRef = useRef(null);
    const { scrollY } = useScroll();
    const cardY = useTransform(scrollY, [0, 700], [0, -48]);
    const seats = BOARD_TABS[0].seats;

    return (
        <main data-testid="home-page">
            {/* ---------- Hero ---------- */}
            <section
                ref={heroRef}
                className="relative overflow-hidden"
                data-testid="hero"
            >
                <div className="wrap flex min-h-[calc(100vh-5rem)] flex-col justify-center py-20 md:py-24">
                    <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
                        <div className="lg:col-span-7">
                            <motion.p {...fadeUp(0.05)} className="eyebrow">
                                Mock interview boards · UPSC · Banking · State PSC
                            </motion.p>

                            <h1
                                className="mt-6 font-display tracking-tight"
                                style={{
                                    fontSize: "clamp(2.75rem, 6.5vw, 5.25rem)",
                                    lineHeight: 1.04,
                                }}
                                data-testid="hero-headline"
                            >
                                <span className="block overflow-hidden pb-1">
                                    <motion.span
                                        className="block"
                                        variants={heroLine}
                                        custom={0}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        Face the board once
                                    </motion.span>
                                </span>
                                <span className="block overflow-hidden pb-2">
                                    <motion.span
                                        className="block"
                                        variants={heroLine}
                                        custom={1}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        before you face the{" "}
                                        <em className="italic text-brass">real one.</em>
                                    </motion.span>
                                </span>
                            </h1>

                            <motion.p
                                {...fadeUp(0.65)}
                                className="mt-6 max-w-xl text-base leading-relaxed text-ink-muted sm:text-lg"
                            >
                                Sit before a panel of retired civil servants,
                                senior bankers and psychologists — and walk into
                                your real interview with a scored report in hand.
                            </motion.p>

                            <motion.div
                                {...fadeUp(0.8)}
                                className="mt-10 flex flex-wrap items-center gap-4 sm:gap-8"
                            >
                                <Link
                                    to="/book"
                                    data-testid="hero-cta"
                                    className="btn-primary"
                                >
                                    Book a mock board
                                    <ArrowRight size={15} strokeWidth={1.5} />
                                </Link>
                                <Link
                                    to="/report"
                                    data-testid="hero-secondary-cta"
                                    className="u-link text-sm tracking-wide text-ink transition-colors duration-300 hover:text-brass"
                                >
                                    See a sample report
                                </Link>
                            </motion.div>
                        </div>

                        {/* Board notice card — subtle parallax */}
                        <motion.div
                            style={{ y: cardY }}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                            className="lg:col-span-5"
                        >
                            <div className="mb-6 border border-line bg-surface" data-testid="hero-video-card">
                                <div className="flex items-center justify-between border-b border-line px-6 py-4">
                                    <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink-muted">
                                        The Panel Room · Film
                                    </p>
                                </div>
                                <video
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    preload="auto"
                                    src="/media/panel-room-ad-v1.mp4"
                                    data-testid="hero-video"
                                    ref={(el) => {
                                        if (el) el.muted = true;
                                    }}
                                    className="w-full"
                                />
                            </div>
                            <div
                                className="border border-line bg-surface"
                                data-testid="board-notice-card"
                            >
                                <div className="flex items-center justify-between border-b border-line px-6 py-4">
                                    <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink-muted">
                                        Today&apos;s board · Room 2
                                    </p>
                                    <SampleTag />
                                </div>
                                <ul>
                                    {seats.map((seat, i) => (
                                        <li
                                            key={seat.id}
                                            className={`flex items-center gap-4 px-6 py-4 ${
                                                i !== seats.length - 1
                                                    ? "border-b border-line"
                                                    : ""
                                            }`}
                                        >
                                            <Armchair
                                                size={18}
                                                strokeWidth={1.25}
                                                className="shrink-0 text-brass"
                                            />
                                            <div>
                                                <p className="font-display text-base leading-snug">
                                                    {seat.name}
                                                </p>
                                                <p className="text-xs text-ink-muted">
                                                    {seat.note}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="flex items-center justify-between border-t border-line px-6 py-4">
                                    <p className="text-xs text-ink-muted">
                                        {REPORT.date} · 30 minutes
                                    </p>
                                    <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-brass">
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-brass" />
                                        Doors close on time
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Ticker />

            {/* ---------- Interactive board-room preview ---------- */}
            <BoardPreview />

            {/* ---------- How it works ---------- */}
            <section className="border-t border-line py-24 md:py-32" data-testid="how-it-works">
                <div className="wrap">
                    <Reveal>
                        <p className="eyebrow">How it works</p>
                        <h2 className="mt-4 max-w-2xl text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Four steps, <em className="italic text-brass">one morning.</em>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                        {HOW_IT_WORKS.map((step, i) => (
                            <Reveal key={step.step} delay={i * 0.08}>
                                <div
                                    className="border-t border-brass/60 pt-6"
                                    data-testid={`how-step-${step.step}`}
                                >
                                    <p className="font-display text-4xl italic text-brass/80 tabular-nums">
                                        {step.step}
                                    </p>
                                    <h3 className="mt-4 text-xl tracking-tight">
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                                        {step.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Sample report preview ---------- */}
            <section className="border-t border-line py-24 md:py-32" data-testid="report-preview">
                <div className="wrap grid gap-14 lg:grid-cols-12 lg:gap-10">
                    <Reveal className="lg:col-span-5">
                        <p className="eyebrow">The evidence</p>
                        <h2 className="mt-4 text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            A report, <em className="italic text-brass">not a shrug.</em>
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-ink-muted">
                            Every board ends the same way: five scores out of
                            ten and a written remark you can act on before the
                            real interview calls.
                        </p>
                        <Link
                            to="/report"
                            data-testid="report-preview-link"
                            className="u-link mt-8 inline-block text-sm tracking-wide text-ink transition-colors duration-300 hover:text-brass"
                        >
                            Open the full sample report
                        </Link>
                    </Reveal>

                    <Reveal delay={0.12} className="lg:col-span-7">
                        <div className="border border-line bg-surface p-7 sm:p-10" data-testid="report-preview-card">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="font-display text-xl">{REPORT.candidate}</p>
                                    <p className="mt-1 text-xs text-ink-muted">{REPORT.exam}</p>
                                </div>
                                <SampleTag />
                            </div>
                            <div className="mt-8 space-y-6">
                                {REPORT.scores.slice(0, 3).map((s, i) => (
                                    <ScoreBar key={s.label} label={s.label} score={s.score} delay={i * 0.1} />
                                ))}
                            </div>
                            <p className="mt-8 border-t border-line pt-6 text-sm leading-relaxed text-ink-muted">
                                “{REPORT.remark.split(". ").slice(0, 2).join(". ")}.”
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ---------- Final CTA ---------- */}
            <section className="bg-pine text-paper" data-testid="final-cta">
                <div className="wrap py-24 text-center md:py-32">
                    <Reveal>
                        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-brass sm:text-xs">
                            Seats open every weekend
                        </p>
                        <h2
                            className="mx-auto mt-6 max-w-3xl font-display tracking-tight"
                            style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)", lineHeight: 1.08 }}
                        >
                            The chair is empty. <em className="italic text-brass">Take it.</em>
                        </h2>
                        <Link
                            to="/book"
                            data-testid="final-cta-btn"
                            className="mt-10 inline-flex items-center justify-center gap-3 bg-brass px-8 py-4 text-[13px] font-medium uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-paper hover:text-pine"
                        >
                            Book a mock board
                            <ArrowRight size={15} strokeWidth={1.5} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default Home;
